import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Pencil, Trash2, Upload, Loader2, X, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { compressImage } from "@/lib/compress-image";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/city-tiles")({
  component: AdminCityTilesPage,
});

export type CityTile = {
  id: string;
  city_slug: string;
  chip: string;
  title: string;
  subtitle: string | null;
  href: string;
  image_url: string | null;
  sort_order: number;
  active: boolean;
};

type CityRow = { slug: string; name: string };

const LINK_OPTIONS = [
  { value: "/train-factory", label: "Train Factory (/train-factory)" },
  { value: "/do", label: "Things to do (/do)" },
  { value: "/eat", label: "Eat & drink (/eat)" },
  { value: "/happening", label: "Events (/happening)" },
  { value: "/search", label: "Search (/search)" },
  { value: "/map", label: "Map (/map)" },
];

function AdminCityTilesPage() {
  const qc = useQueryClient();
  const [cityFilter, setCityFilter] = useState<string>("all");
  const [editing, setEditing] = useState<CityTile | null>(null);
  const [creating, setCreating] = useState(false);

  const citiesQuery = useQuery({
    queryKey: ["admin", "city-tiles", "cities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cities")
        .select("slug, name")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CityRow[];
    },
    staleTime: 300_000,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "city-tiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("city_popular_tiles")
        .select("*")
        .order("city_slug", { ascending: true })
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CityTile[];
    },
  });

  const cities = citiesQuery.data ?? [];
  const rows = useMemo(
    () => (data ?? []).filter((r) => cityFilter === "all" || r.city_slug === cityFilter),
    [data, cityFilter],
  );

  async function toggleActive(row: CityTile) {
    const { error } = await supabase
      .from("city_popular_tiles")
      .update({ active: !row.active })
      .eq("id", row.id);
    if (error) toast.error(error.message);
    else {
      toast.success(row.active ? "Hidden" : "Published");
      refetch();
      qc.invalidateQueries({ queryKey: ["public-city-tiles"] });
    }
  }

  async function remove(row: CityTile) {
    if (!confirm(`Delete "${row.title}"?`)) return;
    const { error } = await supabase.from("city_popular_tiles").delete().eq("id", row.id);
    if (error) toast.error(error.message);
    else {
      toast.success("Deleted");
      refetch();
      qc.invalidateQueries({ queryKey: ["public-city-tiles"] });
    }
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[#495057] hover:text-[#212529]">
              <ArrowLeft className="size-4" /> Admin
            </Link>
            <span className="text-[#adb5bd]">›</span>
            <h1 className="text-xl font-bold text-[#212529]">Popular city tiles</h1>
          </div>
          <button
            onClick={() => { setEditing(null); setCreating(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-[#0d6efd] text-white text-sm font-medium hover:bg-[#0b5ed7]"
          >
            <Plus className="size-4" /> Add tile
          </button>
        </div>

        <p className="text-sm text-[#6c757d] mb-4">
          These are the big cards in the “Popular …” section of every destination page (for example
          “Sauna Experiences” and “Museums &amp; Culture” on the Tampere page). Add a tile per city with
          its own tag, title, subtitle, link and photo. If a city has no tiles here, the default two
          cards are shown.
        </p>

        <div className="mb-3 flex items-center gap-2">
          <label className="text-xs font-semibold text-[#495057]">City</label>
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="px-2.5 py-1.5 border rounded text-sm bg-white"
          >
            <option value="all">All cities</option>
            {cities.map((c) => (
              <option key={c.slug} value={c.slug}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 flex justify-center"><Loader2 className="size-5 animate-spin text-[#6c757d]" /></div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#6c757d]">
              No custom tiles yet — destination pages show the default cards.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead className="bg-[#f1f3f5] text-[11px] uppercase tracking-wider text-[#495057]">
                  <tr>
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Image</th>
                    <th className="px-3 py-2 text-left">City</th>
                    <th className="px-3 py-2 text-left">Tag</th>
                    <th className="px-3 py-2 text-left">Title</th>
                    <th className="px-3 py-2 text-left">Link</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-t hover:bg-[#f8f9fa]">
                      <td className="px-3 py-2 text-[#6c757d]">{row.sort_order}</td>
                      <td className="px-3 py-2">
                        <div
                          className="w-16 h-10 rounded overflow-hidden bg-cover bg-center border bg-[#e9ecef]"
                          style={row.image_url ? { backgroundImage: `url(${row.image_url})` } : undefined}
                        />
                      </td>
                      <td className="px-3 py-2 text-[#495057]">{row.city_slug}</td>
                      <td className="px-3 py-2 text-[#495057] uppercase text-xs">{row.chip || "—"}</td>
                      <td className="px-3 py-2 font-medium text-[#212529]">{row.title}</td>
                      <td className="px-3 py-2 text-[#495057]">{row.href}</td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => toggleActive(row)}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                            row.active ? "bg-[#d1e7dd] text-[#0f5132]" : "bg-[#e9ecef] text-[#6c757d]"
                          }`}
                        >
                          {row.active ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
                          {row.active ? "Live" : "Hidden"}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => { setEditing(row); setCreating(false); }}
                            className="p-1.5 rounded hover:bg-[#e9ecef] text-[#495057]"
                            title="Edit"
                          >
                            <Pencil className="size-4" />
                          </button>
                          <button
                            onClick={() => remove(row)}
                            className="p-1.5 rounded hover:bg-[#f8d7da] text-[#842029]"
                            title="Delete"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {(editing || creating) && (
        <CityTileEditor
          tile={editing}
          cities={cities}
          defaultCity={cityFilter === "all" ? (cities[0]?.slug ?? "") : cityFilter}
          nextSortOrder={rows.length}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            qc.invalidateQueries({ queryKey: ["admin", "city-tiles"] });
            qc.invalidateQueries({ queryKey: ["public-city-tiles"] });
          }}
        />
      )}
    </div>
  );
}

function CityTileEditor({
  tile,
  cities,
  defaultCity,
  nextSortOrder,
  onClose,
  onSaved,
}: {
  tile: CityTile | null;
  cities: CityRow[];
  defaultCity: string;
  nextSortOrder: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<Partial<CityTile>>(() =>
    tile ?? {
      city_slug: defaultCity,
      chip: "",
      title: "",
      subtitle: "",
      href: "/do",
      image_url: "",
      sort_order: nextSortOrder,
      active: true,
    },
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  function up<K extends keyof CityTile>(k: K, v: CityTile[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handleUpload(file: File) {
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const ext = (compressed.name.split(".").pop() || "jpg").toLowerCase();
      const path = `city-tiles/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from("content-images").upload(path, compressed, {
        cacheControl: "31536000",
        upsert: false,
        contentType: compressed.type || "image/jpeg",
      });
      if (error) throw error;
      const { data } = supabase.storage.from("content-images").getPublicUrl(path);
      up("image_url", data.publicUrl);
      toast.success("Image uploaded");
    } catch (err: any) {
      toast.error(err?.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    if (!form.city_slug) { toast.error("Pick a city"); return; }
    if (!form.title?.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        city_slug: form.city_slug,
        chip: (form.chip ?? "").trim(),
        title: form.title!.trim(),
        subtitle: form.subtitle?.trim() || null,
        href: form.href?.trim() || "/do",
        image_url: form.image_url?.trim() || null,
        sort_order: Number(form.sort_order ?? 0),
        active: form.active ?? true,
      };
      if (tile) {
        const { error } = await supabase.from("city_popular_tiles").update(payload).eq("id", tile.id);
        if (error) throw error;
        toast.success("Saved");
      } else {
        const { error } = await supabase.from("city_popular_tiles").insert(payload);
        if (error) throw error;
        toast.success("Created");
      }
      onSaved();
    } catch (err: any) {
      toast.error(err?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-lg shadow-xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10">
          <h2 className="font-semibold text-[#212529]">{tile ? "Edit tile" : "New tile"}</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#e9ecef]"><X className="size-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="text-xs font-semibold text-[#495057]">Image</label>
            <div className="mt-1.5 flex items-start gap-3">
              <div
                className="w-40 h-24 rounded-lg overflow-hidden bg-cover bg-center border bg-[#e9ecef] flex-shrink-0"
                style={form.image_url ? { backgroundImage: `url(${form.image_url})` } : undefined}
              />
              <div className="flex-1 min-w-0 space-y-2">
                <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-[#e9ecef] text-[#212529] text-sm font-medium cursor-pointer hover:bg-[#dee2e6]">
                  {uploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                  {uploading ? "Uploading…" : (form.image_url ? "Replace image" : "Upload image")}
                  <input
                    type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.currentTarget.value = ""; }}
                  />
                </label>
                {form.image_url && (
                  <button onClick={() => up("image_url", "")} className="text-xs text-[#dc3545] hover:underline block">
                    Remove image (use a place photo automatically)
                  </button>
                )}
                <p className="text-[11px] text-[#6c757d]">Wide 16:10 photos work best. Auto-compressed on upload.</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="City *">
              <select
                className="w-full px-2.5 py-1.5 border rounded text-sm bg-white"
                value={form.city_slug ?? ""}
                onChange={(e) => up("city_slug", e.target.value)}
              >
                <option value="">Select city…</option>
                {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Tag (small white chip)">
              <input
                className="w-full px-2.5 py-1.5 border rounded text-sm"
                value={form.chip ?? ""}
                onChange={(e) => up("chip", e.target.value)}
                placeholder="sauna"
              />
            </Field>
            <Field label="Title *">
              <input
                className="w-full px-2.5 py-1.5 border rounded text-sm"
                value={form.title ?? ""}
                onChange={(e) => up("title", e.target.value)}
                placeholder="Sauna experiences"
              />
            </Field>
            <Field label="Subtitle">
              <input
                className="w-full px-2.5 py-1.5 border rounded text-sm"
                value={form.subtitle ?? ""}
                onChange={(e) => up("subtitle", e.target.value)}
                placeholder="Find your perfect moment"
              />
            </Field>
            <Field label="Links to">
              <select
                className="w-full px-2.5 py-1.5 border rounded text-sm bg-white"
                value={LINK_OPTIONS.some((o) => o.value === form.href) ? form.href : "custom"}
                onChange={(e) => up("href", e.target.value === "custom" ? "" : e.target.value)}
              >
                {LINK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                <option value="custom">Custom path…</option>
              </select>
              {!LINK_OPTIONS.some((o) => o.value === form.href) && (
                <input
                  className="mt-2 w-full px-2.5 py-1.5 border rounded text-sm"
                  value={form.href ?? ""}
                  onChange={(e) => up("href", e.target.value)}
                  placeholder="/do?category=sauna"
                />
              )}
            </Field>
            <Field label="Sort order">
              <input
                type="number"
                className="w-full px-2.5 py-1.5 border rounded text-sm"
                value={form.sort_order ?? 0}
                onChange={(e) => up("sort_order", Number(e.target.value))}
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active ?? true}
              onChange={(e) => up("active", e.target.checked)}
            />
            <span>Published (visible on the destination page)</span>
          </label>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-4 py-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm text-[#495057] hover:bg-[#f1f3f5]">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#0d6efd] text-white text-sm font-medium hover:bg-[#0b5ed7] disabled:opacity-60"
          >
            {saving && <Loader2 className="size-4 animate-spin" />}
            {tile ? "Save changes" : "Create tile"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-semibold text-[#495057] block mb-1">{label}</label>
      {children}
    </div>
  );
}
